import React, { useEffect, useMemo, useRef, useState } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import GetAppIcon from '@material-ui/icons/GetApp';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import { Modal } from 'react-bootstrap';
import { getResourceCenter } from '../Services/resourceCenterAPI';
import SecureVideoPlayer from './SecureVideoPlayer/SecureVideoPlayer';
import '../../Assets/CSS/FullModal.css';
import './ResourceCenter.css';

const NAVY = '#1a2c40';

const styles = {
    // Fixed-height tab: the search bar stays put and only the section list scrolls.
    page: { backgroundColor: '#e6e9f2', minHeight: 320, display: 'flex', flexDirection: 'column', fontFamily: 'Roboto, Arial, sans-serif' },
    scroll: { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', paddingBottom: 24 },
    // Popup chrome matches the "Program Details" dialog: title + expand/close in the header,
    // outline Download + filled Close in the footer.
    popupHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px 10px 24px', borderBottom: '1px solid #e5e5e5' },
    popupTitle: { margin: 0, minWidth: 0, paddingRight: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 20, fontWeight: 500, lineHeight: 1.2, color: '#222', fontFamily: 'Roboto, Arial, sans-serif' },
    popupIconBtn: { width: 40, height: 40, marginLeft: 8, border: 'none', borderRadius: '50%', background: 'transparent', color: '#222', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
    popupFooter: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '10px 24px', borderTop: '1px solid #e5e5e5' },
    downloadBtn: { height: 40, padding: '0 22px', marginRight: 10, borderRadius: 50, border: `solid 1px ${NAVY}`, backgroundColor: '#fff', color: NAVY, fontSize: 16, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', fontFamily: 'Roboto, Arial, sans-serif' },
    closeBtn: { height: 40, minWidth: 96, padding: '0 26px', borderRadius: 50, border: `solid 1px ${NAVY}`, backgroundColor: NAVY, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'Roboto, Arial, sans-serif' },
    pdfFrame: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' },
    searchRow: { flex: '0 0 auto', display: 'flex', alignItems: 'center', padding: '16px 16px 14px' },
    searchBox: { position: 'relative', width: 440, maxWidth: '60%' },
    suggestList: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, margin: '2px 0 0', padding: '4px 0', listStyle: 'none', backgroundColor: '#fff', border: '1px solid #e0e3ec', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 280, overflowY: 'auto' },
    suggestItem: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '8px 14px', cursor: 'pointer', fontSize: 15, color: '#222', fontFamily: 'Roboto, Arial, sans-serif' },
    suggestHint: { marginLeft: 12, fontSize: 12, color: '#8a939c', whiteSpace: 'nowrap' },
    clearBtn: { position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 28, height: 28, border: 'none', borderRadius: '50%', background: 'transparent', color: '#6b7680', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
    searchLabel: { position: 'absolute', left: 14, pointerEvents: 'none', color: '#6b7680', fontFamily: 'Roboto, Arial, sans-serif', transition: 'all 0.15s ease' },
    searchInput: { width: '100%', height: 48, boxSizing: 'border-box', border: '1px solid #e0e3ec', borderRadius: 2, padding: '16px 40px 0 14px', fontSize: 15, outline: 'none', backgroundColor: '#fff', fontFamily: 'inherit' },
    searchBtn: { width: 44, height: 44, marginLeft: 12, borderRadius: '50%', border: 'none', backgroundColor: NAVY, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    columns: { display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' },
    column: { flex: '1 1 420px', minWidth: 0 },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', border: 'none', borderBottom: '2px solid #e6e9f2', backgroundColor: '#c9d3dd', color: NAVY, fontSize: 18, padding: '0 24px', height: 52, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' },
    body: { backgroundColor: '#fff', maxHeight: '40vh', overflowY: 'auto', padding: '14px 24px 10px' },
    subTitle: { margin: '4px 0 6px', fontSize: 18, fontWeight: 700, color: '#222' },
    item: { display: 'flex', alignItems: 'center', padding: '6px 0', cursor: 'pointer', color: '#222', fontSize: 17, background: 'none', border: 'none', width: '100%', textAlign: 'left', fontFamily: 'inherit' },
    itemIcon: { color: '#7d8590', marginRight: 14, fontSize: 28 },
    empty: { color: '#888', fontSize: 14, padding: '4px 0 10px' },
};

// Sample data has a stray encoded quote at the end of one URL (".pdf%22") -- strip it so the
// link opens.
const cleanUrl = (url = '') => String(url).trim().replace(/(%22|["'])+$/, '');

const isVideo = (item) => /video|mp4|youtube|vimeo/i.test(item.resourceType || '') || /vimeo\.com/i.test(item.resourceUrl || '');

// Vimeo page links (vimeo.com/<id> or vimeo.com/<id>/<hash>) can't play in a <video> tag; they
// need Vimeo's player embed. Returns that embed URL, or null for any other link.
const getVimeoEmbedUrl = (url = '') => {
    if (/player\.vimeo\.com\/video\//i.test(url)) return url;
    const m = String(url).match(/vimeo\.com\/(?:(?:channels|groups)\/[^/]+\/(?:videos\/)?)?(\d+)(?:\/([a-z0-9]+))?/i);
    if (!m) return null;
    return `https://player.vimeo.com/video/${m[1]}${m[2] ? `?h=${m[2]}` : ''}`;
};

const bySequence = (a, b) => (a.sequence || 0) - (b.sequence || 0);
const byLabel = (a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' });

const ResourceItem = ({ item, onOpen }) => (
    <button type="button" style={styles.item} onClick={() => onOpen(item)}>
        {isVideo(item)
            ? <PlayCircleOutlineIcon style={styles.itemIcon} />
            : <InsertDriveFileOutlinedIcon style={styles.itemIcon} />}
        <span>{item.label}</span>
    </button>
);

const ResourceCenter = () => {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [text, setText] = useState('');
    const [focused, setFocused] = useState(false);
    const [activeSuggestion, setActiveSuggestion] = useState(-1);
    const [dismissed, setDismissed] = useState(false);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState({});
    const [viewer, setViewer] = useState(null);
    const pageRef = useRef(null);
    const [pageHeight, setPageHeight] = useState(null);

    // Fixed-height tab: fill exactly the space from the top of this tab to the bottom of the
    // window, so the whole accordion set scrolls with one scrollbar (never inside an accordion,
    // and never a second scrollbar from the page behind it).
    useEffect(() => {
        const fit = () => {
            if (!pageRef.current) return;
            const top = pageRef.current.getBoundingClientRect().top;
            setPageHeight(Math.max(320, Math.floor(window.innerHeight - top - 12)));
        };
        fit();
        const t = setTimeout(fit, 300);
        window.addEventListener('resize', fit);
        return () => { clearTimeout(t); window.removeEventListener('resize', fit); };
    }, []);
    const [full, setFull] = useState(false);

    useEffect(() => {
        let alive = true;
        getResourceCenter()
            .then((data) => { if (alive) { setSections(Array.isArray(data) ? [...data].sort(byLabel) : []); setLoading(false); } })
            .catch(() => { if (alive) { setError('Unable to load resources. Please try again later.'); setLoading(false); } });
        return () => { alive = false; };
    }, []);

    const needle = query.trim().toLowerCase();
    // Every section returned by the API is shown (even with no items yet); sections not in the
    // response simply don't appear. While searching, a section stays if its own label matches
    // (with all its resources), or if a sub-section label or an item label matches (only those).
    const visible = useMemo(() => {
        if (!needle) return sections;
        const has = (label) => (label || '').toLowerCase().includes(needle);
        return sections.map((section) => {
            if (has(section.label)) return section;
            const items = (section.items || []).filter((item) => has(item.label));
            const subSections = (section.subSections || [])
                .map((sub) => (has(sub.label) ? sub : { ...sub, items: (sub.items || []).filter((item) => has(item.label)) }))
                .filter((sub) => has(sub.label) || sub.items.length > 0);
            return { ...section, items, subSections };
        }).filter((section) => has(section.label) || section.items.length > 0 || section.subSections.length > 0);
    }, [sections, needle]);

    // `path` = the section (product) name and, if any, the sub-section name, so the popup title
    // tells which product the document belongs to.
    const openItem = (item, path = []) => {
        const url = cleanUrl(item.resourceUrl);
        if (!url) return;
        setFull(false);
        setViewer({ url, title: [...path, item.label].filter(Boolean).join(' - '), type: isVideo(item) ? 'video' : 'pdf' });
    };

    // The file host doesn't allow cross-origin downloads, so Download opens the PDF in a new tab
    // (called directly from the click so the browser doesn't block it as a popup).
    const downloadPdf = () => {
        if (viewer) window.open(viewer.url, '_blank', 'noopener,noreferrer');
    };

    // While searching, every matching section is shown expanded.
    const isOpen = (id) => (needle ? true : !!open[id]);
    // Only one accordion is open at a time: opening one closes the others.
    const toggle = (id) => setOpen((prev) => (prev[id] ? {} : { [id]: true }));
    const applySearch = (value = text) => { setQuery(value); setActiveSuggestion(-1); };
    const clearSearch = () => { setText(''); setQuery(''); setActiveSuggestion(-1); };

    // Auto-complete: section, sub-section and item names containing what was typed (names that
    // start with it first), shown under the box while it has focus and the text isn't yet applied.
    const typed = text.trim().toLowerCase();
    const suggestions = useMemo(() => {
        if (!typed) return [];
        const found = new Map();
        const add = (label, hint) => {
            const key = (label || '').trim().toLowerCase();
            if (!key || !key.includes(typed) || found.has(key)) return;
            found.set(key, { label: label.trim(), hint, starts: key.startsWith(typed) });
        };
        sections.forEach((section) => {
            add(section.label, '');
            (section.items || []).forEach((item) => add(item.label, section.label));
            (section.subSections || []).forEach((sub) => {
                add(sub.label, section.label);
                (sub.items || []).forEach((item) => add(item.label, `${section.label} - ${sub.label}`));
            });
        });
        return [...found.values()].sort((a, b) => (b.starts - a.starts) || a.label.localeCompare(b.label)).slice(0, 8);
    }, [sections, typed]);
    const showSuggestions = focused && !dismissed && suggestions.length > 0 && text.trim() !== query.trim();
    const pickSuggestion = (label) => { setText(label); applySearch(label); };

    // Two independent columns (accordions don't stretch each other). `visible` is already sorted
    // by name; sections are dealt out alternately (1st left, 2nd right, 3rd left, ...) so the
    // order reads A-Z across each row as well as down each column. The left column gets the extra
    // one when the count is odd (6 -> 3 + 3, 7 -> 4 + 3) and it rebalances when sections change.
    const columns = [visible.filter((_, i) => i % 2 === 0), visible.filter((_, i) => i % 2 === 1)];

    const renderSection = (section) => {
        const expanded = isOpen(section.id);
        const items = [...(section.items || [])].sort(bySequence);
        const subSections = [...(section.subSections || [])].sort(bySequence);
        const hasContent = items.length > 0 || subSections.some((s) => (s.items || []).length > 0);
        return (
            <div key={section.id}>
                <button type="button" style={styles.header} aria-expanded={expanded} onClick={() => toggle(section.id)}>
                    <span>{section.label}</span>
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expanded && (
                    <div style={styles.body}>
                        {!hasContent && <div style={styles.empty}>No resources available.</div>}
                        {items.map((item) => <ResourceItem key={`i${item.id}`} item={item} onOpen={(it) => openItem(it, [section.label])} />)}
                        {subSections.map((sub) => (
                            <div key={`s${sub.id}`}>
                                <div style={styles.subTitle}>{sub.label}</div>
                                {[...(sub.items || [])].sort(bySequence).map((item) => (
                                    <ResourceItem key={`i${item.id}`} item={item} onOpen={(it) => openItem(it, [section.label, sub.label])} />
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div ref={pageRef} style={{ ...styles.page, ...(pageHeight ? { height: pageHeight } : { height: 'calc(100vh - 170px)' }) }}>
            <div style={styles.searchRow}>
                <div style={styles.searchBox}>
                    <label
                        htmlFor="resource-search"
                        style={{ ...styles.searchLabel, ...(focused || text ? { top: 6, fontSize: 11, color: focused ? NAVY : '#6b7680' } : { top: 14, fontSize: 15 }) }}
                    >
                        Search Resources
                    </label>
                    <input
                        id="resource-search"
                        style={styles.searchInput}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        type="text"
                        value={text}
                        aria-label="Search resources"
                        autoComplete="off"
                        role="combobox"
                        aria-expanded={showSuggestions}
                        aria-autocomplete="list"
                        onChange={(e) => { setText(e.target.value); setActiveSuggestion(-1); setDismissed(false); if (!e.target.value) setQuery(''); }}
                        onKeyDown={(e) => {
                            if (e.key === 'ArrowDown' && showSuggestions) {
                                e.preventDefault();
                                setActiveSuggestion((i) => (i + 1) % suggestions.length);
                            } else if (e.key === 'ArrowUp' && showSuggestions) {
                                e.preventDefault();
                                setActiveSuggestion((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
                            } else if (e.key === 'Enter') {
                                if (showSuggestions && activeSuggestion >= 0) pickSuggestion(suggestions[activeSuggestion].label);
                                else applySearch();
                            } else if (e.key === 'Escape') {
                                setActiveSuggestion(-1);
                                setDismissed(true);
                            }
                        }}
                    />
                    {showSuggestions && (
                        <ul style={styles.suggestList} role="listbox">
                            {suggestions.map((sug, i) => (
                                <li
                                    key={sug.label}
                                    role="option"
                                    aria-selected={i === activeSuggestion}
                                    style={{ ...styles.suggestItem, backgroundColor: i === activeSuggestion ? '#eef1f7' : '#fff' }}
                                    onMouseEnter={() => setActiveSuggestion(i)}
                                    onMouseDown={(e) => { e.preventDefault(); pickSuggestion(sug.label); }}
                                >
                                    <span>{sug.label}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {text && (
                        <button type="button" style={styles.clearBtn} aria-label="Clear search" title="Clear search" onClick={clearSearch}>
                            <CloseIcon style={{ fontSize: 20 }} />
                        </button>
                    )}
                </div>
                <button type="button" style={styles.searchBtn} aria-label="Search" onClick={() => applySearch()}>
                    <SearchIcon />
                </button>
            </div>
            {loading && <div style={{ ...styles.empty, padding: '8px 24px' }}>Loading resources...</div>}
            {error && <div style={{ ...styles.empty, padding: '8px 24px', color: '#c62828' }}>{error}</div>}
            {!loading && !error && visible.length === 0 && (
                <div style={{ ...styles.empty, padding: '8px 24px' }}>{needle ? 'No resources match your search.' : 'No resources available.'}</div>
            )}
            <div style={styles.scroll}>
                <div style={styles.columns}>
                    {columns.map((col, i) => <div key={i} style={styles.column}>{col.map(renderSection)}</div>)}
                </div>
            </div>
            {viewer && (
                <Modal
                    show
                    onHide={() => setViewer(null)}
                    centered
                    dialogClassName={full ? 'modal-fullscreen' : (viewer.type === 'pdf' ? 'resource-pdf-dialog' : 'resource-video-dialog')}
                >
                    <div style={styles.popupHeader}>
                        <h4 style={styles.popupTitle}>{viewer.title}</h4>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <button type="button" style={styles.popupIconBtn} aria-label={full ? 'Exit full screen' : 'Full screen'} onClick={() => setFull((f) => !f)}>
                                {full ? <FullscreenExitIcon style={{ fontSize: 28 }} /> : <FullscreenIcon style={{ fontSize: 28 }} />}
                            </button>
                            <button type="button" style={styles.popupIconBtn} aria-label="Close" onClick={() => setViewer(null)}>
                                <CloseIcon style={{ fontSize: 28 }} />
                            </button>
                        </div>
                    </div>
                    <Modal.Body style={viewer.type === 'pdf' ? { padding: 0, flex: '1 1 auto', minHeight: 0, position: 'relative' } : { padding: 0 }}>
                        {viewer.type === 'pdf'
                            ? <iframe title={viewer.title} src={`${viewer.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} style={styles.pdfFrame} />
                            : (getVimeoEmbedUrl(viewer.url)
                                ? <iframe
                                    title={viewer.title}
                                    src={getVimeoEmbedUrl(viewer.url)}
                                    style={{ width: '100%', aspectRatio: '16 / 9', border: 'none', display: 'block' }}
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                />
                                : <SecureVideoPlayer url={viewer.url} title={viewer.title || ''} onComplete={() => {}} />)}
                    </Modal.Body>
                    {viewer.type === 'pdf' && (
                        <div style={styles.popupFooter}>
                            <button type="button" style={styles.downloadBtn} onClick={downloadPdf}>
                                <GetAppIcon style={{ fontSize: 22, marginRight: 8 }} />Download
                            </button>
                            <button type="button" style={styles.closeBtn} onClick={() => setViewer(null)}>Close</button>
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
};

export default ResourceCenter;
